import { ConversationsV2UserMessageInput } from '@spotify/confidence-graphql';

import { DocAttachment, PendingAttachment, ResolvedAttachment } from '../types';

export const buildUserMessage = (
  content: string,
  attachments: ResolvedAttachment[],
  imageUris: string[] = [],
): ConversationsV2UserMessageInput => {
  const hasAttachments = attachments.length > 0 || imageUris.length > 0;
  if (!hasAttachments) return { content };

  return {
    parts: [
      ...attachments.map(attachment => ({ resource: attachment.name })),
      ...imageUris.map(uri => ({ imageUri: uri })),
      ...(content ? [{ text: content }] : []),
    ],
  };
};

export const appendPendingAttachmentUrlsToContent = (
  content: string,
  attachments: DocAttachment[],
): string | undefined => {
  const pendingAttachments = attachments.filter(
    (attachment): attachment is PendingAttachment =>
      attachment.kind === 'pending',
  );
  const pendingUrls = pendingAttachments
    .map(attachment => attachment.uri)
    .filter((uri): uri is string => Boolean(uri));

  if (
    pendingUrls.length === 0 ||
    pendingUrls.length !== pendingAttachments.length
  ) {
    return undefined;
  }

  return [content, ...pendingUrls].filter(Boolean).join('\n');
};

type ResolveAttachmentsFn = (
  chatName: string,
  attachments: DocAttachment[],
) => Promise<ResolvedAttachment[]>;

export const resolveAttachmentsWithFallback = async (
  content: string,
  chatName: string,
  attachments: DocAttachment[],
  resolveAttachments: ResolveAttachmentsFn,
): Promise<{
  messageContent: string;
  sentAttachments: ResolvedAttachment[];
}> => {
  const hasPending = attachments.some(a => a.kind === 'pending');
  if (!hasPending) {
    return {
      messageContent: content,
      sentAttachments: attachments.filter(
        (a): a is ResolvedAttachment => a.kind === 'resolved',
      ),
    };
  }
  try {
    return {
      messageContent: content,
      sentAttachments: await resolveAttachments(chatName, attachments),
    };
  } catch (err) {
    const fallbackContent = appendPendingAttachmentUrlsToContent(
      content,
      attachments,
    );
    // No URI on a pending attachment means there's nothing to fall back to.
    if (!fallbackContent) throw err;
    return {
      messageContent: fallbackContent,
      sentAttachments: attachments.filter(
        (a): a is ResolvedAttachment => a.kind === 'resolved',
      ),
    };
  }
};
